import { hasAsciiControlOrSpace, isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export type WebsitePublicationFieldState =
  | 'OBSERVED'
  | 'NULL'
  | 'EMPTY'
  | 'MISSING'
  | 'QUARANTINED';
export type WebsitePublicationRecordId = number | string;
export interface WebsitePublicationSourceField<T> {
  state: WebsitePublicationFieldState;
  source: { table: string; record_id: WebsitePublicationRecordId | null; field: string };
  value: T | null;
}
export interface WebsitePublicationImage {
  state: 'OBSERVED' | 'QUARANTINED';
  source: {
    table: 'web_pg.branch_exterior_images' | 'web_pg.branch_detail_images';
    record_id: WebsitePublicationRecordId;
    field: 'image_url';
  };
  value: string | null;
  renderable?: boolean;
  quarantineReason?: 'frontend-uri-check' | 'server-uri-policy' | null;
  is_active: WebsitePublicationSourceField<boolean>;
  display_order: number;
  content_slot?: WebsitePublicationSourceField<string>;
}
export interface WebsitePublication {
  source: {
    mode: 'WEB_PUBLICATION_OBSERVATION';
    status: 'AVAILABLE';
    namespace: string;
    observed_at: string;
  };
  property: {
    id: string;
    source_branch_id: number;
    source_environment: 'dev' | 'prod';
    core_version: number;
  };
  publication: {
    is_published: WebsitePublicationSourceField<boolean>;
    tagline: {
      ko: WebsitePublicationSourceField<string>;
      en: WebsitePublicationSourceField<string>;
    };
  };
  images: { exterior: WebsitePublicationImage[]; detail: WebsitePublicationImage[] };
}
export interface WebsitePublicationBinding {
  propertyId: string;
  branchId: number;
  environment: 'dev' | 'prod';
  version: number;
}

const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const hasKeys = (value: Record<string, unknown>, keys: readonly string[]) =>
  Object.keys(value).length === keys.length &&
  keys.every((key) => Object.prototype.hasOwnProperty.call(value, key));
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '게시 정보를 불러오지 못했습니다. 관리자에게 문의해 주세요.',
  });
const states = new Set<WebsitePublicationFieldState>([
  'OBSERVED',
  'NULL',
  'EMPTY',
  'MISSING',
  'QUARANTINED',
]);

function sourceField<T>(
  value: unknown,
  table: string,
  field: string,
  accepts: (candidate: unknown) => candidate is T,
): value is WebsitePublicationSourceField<T> {
  if (
    !record(value) ||
    !hasKeys(value, ['state', 'source', 'value']) ||
    !states.has(value.state as WebsitePublicationFieldState) ||
    !record(value.source) ||
    !hasKeys(value.source, ['table', 'record_id', 'field']) ||
    value.source.table !== table ||
    value.source.field !== field ||
    !(value.source.record_id === null || recordId(value.source.record_id))
  )
    return false;
  if (value.state === 'MISSING') return value.source.record_id === null && value.value === null;
  if (value.source.record_id === null) return false;
  if (value.state === 'NULL' || value.state === 'QUARANTINED') return value.value === null;
  if (value.state === 'EMPTY') return value.value === '';
  return accepts(value.value);
}

const text = (value: unknown): value is string => typeof value === 'string';
const boolean = (value: unknown): value is boolean => typeof value === 'boolean';
const signedBigint = /^(?:0|-[1-9]\d*|[1-9]\d*)$/;
const bigintMin = -9223372036854775808n;
const bigintMax = 9223372036854775807n;
function recordId(value: unknown): value is WebsitePublicationRecordId {
  if (typeof value === 'number') return Number.isSafeInteger(value);
  if (typeof value !== 'string' || value.length > 20 || !signedBigint.test(value)) return false;
  const parsed = BigInt(value);
  return parsed >= bigintMin && parsed <= bigintMax;
}
function safeImageUri(value: unknown): value is string {
  if (
    typeof value !== 'string' ||
    new TextEncoder().encode(value).length > 8192 ||
    hasAsciiControlOrSpace(value)
  )
    return false;
  try {
    const uri = new URL(value);
    if (uri.protocol !== 'https:' || !uri.hostname || uri.username || uri.password || uri.hash)
      return false;
    if (!uri.search) return true;
    if (!['my.matterport.com', 'matterport.com'].includes(uri.hostname.toLowerCase())) return false;
    const pairs = [...uri.searchParams.entries()];
    if (pairs.length !== 1) return false;
    const [rawName, rawValue] = pairs[0] ?? [];
    if (rawName === undefined || rawValue === undefined) return false;
    const decodeAgain = (part: string) => {
      try {
        return decodeURIComponent(part);
      } catch {
        return part;
      }
    };
    const name = decodeAgain(rawName).trim().toLowerCase().replaceAll('-', '_');
    const model = decodeAgain(rawValue).trim();
    if (name !== 'm' || !model || model.length > 256) return false;
    if (
      /(?:token|signature|sig|credential|api[_-]?key|access[_-]?key|password|passwd|secret|auth|x_amz|expires)/i.test(
        name,
      )
    )
      return false;
    return !/(?:token|signature|credential|api[_-]?key|access[_-]?key|password|passwd|secret|auth)\s*[:=]/i.test(
      model,
    );
  } catch {
    return false;
  }
}

function image(value: unknown, kind: 'exterior' | 'detail'): value is WebsitePublicationImage {
  const table =
    kind === 'exterior' ? 'web_pg.branch_exterior_images' : 'web_pg.branch_detail_images';
  if (
    !record(value) ||
    !hasKeys(
      value,
      kind === 'detail'
        ? ['state', 'source', 'value', 'is_active', 'display_order', 'content_slot']
        : ['state', 'source', 'value', 'is_active', 'display_order'],
    ) ||
    !record(value.source) ||
    !hasKeys(value.source, ['table', 'record_id', 'field']) ||
    value.source.table !== table ||
    value.source.field !== 'image_url' ||
    !recordId(value.source.record_id) ||
    !Number.isSafeInteger(value.display_order) ||
    !sourceField(value.is_active, table, 'is_active', boolean)
  )
    return false;
  const validUri = value.state === 'OBSERVED' && typeof value.value === 'string';
  const quarantined = value.state === 'QUARANTINED' && value.value === null;
  if (!validUri && !quarantined) return false;
  return kind === 'exterior'
    ? !Object.prototype.hasOwnProperty.call(value, 'content_slot')
    : sourceField(value.content_slot, table, 'content_slot', text);
}

function valid(value: unknown, binding: WebsitePublicationBinding): value is WebsitePublication {
  if (
    !record(value) ||
    !record(value.source) ||
    !record(value.property) ||
    !record(value.publication) ||
    !record(value.images)
  )
    return false;
  if (
    !hasKeys(value, ['source', 'property', 'publication', 'images']) ||
    !hasKeys(value.source, ['mode', 'status', 'namespace', 'observed_at']) ||
    !hasKeys(value.property, ['id', 'source_branch_id', 'source_environment', 'core_version']) ||
    !hasKeys(value.publication, ['is_published', 'tagline']) ||
    !record(value.publication.tagline) ||
    !hasKeys(value.publication.tagline, ['ko', 'en']) ||
    !hasKeys(value.images, ['exterior', 'detail'])
  )
    return false;
  const source = value.source;
  const property = value.property;
  const publication = value.publication;
  const tagline = publication.tagline as Record<string, unknown>;
  const namespace = `web_pg/${binding.environment}/web_mesh/public`;
  if (
    source.mode !== 'WEB_PUBLICATION_OBSERVATION' ||
    source.status !== 'AVAILABLE' ||
    source.namespace !== namespace ||
    !isOffsetDateTime(source.observed_at) ||
    !isUuid(property.id) ||
    property.id.toLowerCase() !== binding.propertyId.toLowerCase() ||
    property.source_branch_id !== binding.branchId ||
    property.source_environment !== binding.environment ||
    property.core_version !== binding.version ||
    !sourceField(publication.is_published, 'web_pg.branch_contents', 'is_published', boolean) ||
    !sourceField(tagline.ko, 'web_pg.branch_contents', 'tagline_ko', text) ||
    !sourceField(tagline.en, 'web_pg.branch_contents', 'tagline_en', text) ||
    !Array.isArray(value.images.exterior) ||
    value.images.exterior.length > 64 ||
    !value.images.exterior.every((item) => image(item, 'exterior')) ||
    !Array.isArray(value.images.detail) ||
    value.images.detail.length > 64 ||
    !value.images.detail.every((item) => image(item, 'detail'))
  )
    return false;
  return true;
}

export async function readWebsitePublication(
  client: ServiceClient,
  binding: WebsitePublicationBinding,
  signal?: AbortSignal,
): Promise<WebsitePublication> {
  if (
    !isUuid(binding.propertyId) ||
    !Number.isSafeInteger(binding.branchId) ||
    binding.branchId <= 0 ||
    !Number.isSafeInteger(binding.version) ||
    binding.version < 0 ||
    !['dev', 'prod'].includes(binding.environment)
  )
    throw invalid();
  const body = await client.get<unknown>(
    `admin/properties/${binding.propertyId}/website-publication`,
    { signal },
  );
  if (!valid(body, binding)) throw invalid();
  return {
    ...body,
    images: {
      exterior: body.images.exterior.map((item) =>
        item.state === 'OBSERVED' && !safeImageUri(item.value)
          ? {
              ...item,
              value: null,
              renderable: false,
              quarantineReason: 'frontend-uri-check',
            }
          : {
              ...item,
              renderable: item.state === 'OBSERVED',
              quarantineReason: item.state === 'QUARANTINED' ? 'server-uri-policy' : null,
            },
      ),
      detail: body.images.detail.map((item) =>
        item.state === 'OBSERVED' && !safeImageUri(item.value)
          ? {
              ...item,
              value: null,
              renderable: false,
              quarantineReason: 'frontend-uri-check',
            }
          : {
              ...item,
              renderable: item.state === 'OBSERVED',
              quarantineReason: item.state === 'QUARANTINED' ? 'server-uri-policy' : null,
            },
      ),
    },
  };
}
